import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { useAuth } from "../Context/AuthProvider";
import { tokens, withAlpha } from "../Common/designTokens";
import FixLogsTable from "../Component/FixLogs/FixLogsTable";
import QuickFixFilterModal from "../Component/FixLogs/QuickFixFilterModal";
import FixMessageDecoderModal from "../Component/FixLogs/FixMessageDecoderModal";
import DownloadNoLogsPopup from "../Component/FixLogs/DownloadNoLogsPopup";
import { getFixLogColumns, formatFixTimestamp, downloadFixLogs } from "../Component/FixLogs/FixLogsUtils";
import { fetchQuickFixEvents, fetchQuickFixMessages } from "../http/api";
import QuickFixSessionStatus from "../Component/FixLogs/QuickFixSessionStatus";
import dayjs from "dayjs";
import { utcToLocal } from "../Utils/timeConversions";
import FilterChips from "../Component/InventoryComponents/FilterChips";
import { useQuickFixFilterStore, getDefaultPast24hUtcRange } from "../Store/quickFixFilterStore";
import QuickFixDownloadConfirmModal from "../Component/FixLogs/QuickFixDownloadConfirmModal";

const MAX_DOWNLOAD_RECORDS = 100000;

const TAB_CONFIG = {
  messages: { label: "Message Logs", fetcher: fetchQuickFixMessages },
  events: { label: "Event Logs", fetcher: fetchQuickFixEvents },
};

const QuickFixLogsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("messages");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "time",
    direction: "desc",
  });
  const filters = useQuickFixFilterStore((state) => state.filters);
  const excludeHeartbeat = useQuickFixFilterStore((state) => state.excludeHeartbeat);
  const hasActiveFilters = useQuickFixFilterStore((state) => state.hasActiveFilters);
  const setFilters = useQuickFixFilterStore((state) => state.setFilters);
  const setExcludeHeartbeat = useQuickFixFilterStore((state) => state.setExcludeHeartbeat);
  const initializeHeartbeatExclusion = useQuickFixFilterStore((state) => state.initializeHeartbeatExclusion);
  const isFilterStoreInitialized = useQuickFixFilterStore((state) => state.isInitialized);
  const clearFilters = useQuickFixFilterStore((state) => state.clearFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const removeFilter = useQuickFixFilterStore((state) => state.removeFilter);
  const columns = useMemo(() => getFixLogColumns(), []);
  const currentFetcher = useMemo(
    () => TAB_CONFIG[activeTab].fetcher,
    [activeTab]
  );
  const [decoderModalOpen, setDecoderModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sessionRefreshTrigger, setSessionRefreshTrigger] = useState(0);
  const [csvLoading, setCsvLoading] = useState(false);
  const [noLogsDialogOpen, setNoLogsDialogOpen] = useState(false);
  const [noLogsAppliedFilters, setNoLogsAppliedFilters] = useState([]);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState("xlsx");

  const sortedRows = useMemo(() => {
    if (!sortConfig?.key || rows.length === 0) return rows;
    const normalizeToUtc = (value) => {
      if (!value) return "";
      return value.endsWith("Z") ? value : `${value}Z`;
    };
    const copy = [...rows];
    copy.sort((a, b) => {
      const aVal = a?.[sortConfig.key];
      const bVal = b?.[sortConfig.key];
      if (sortConfig.key === "time") {
        const aTime = Date.parse(normalizeToUtc(aVal));
        const bTime = Date.parse(normalizeToUtc(bVal));
        const delta = (aTime || 0) - (bTime || 0);
        return sortConfig.direction === "asc" ? delta : -delta;
      }
      const aStr = (aVal ?? "").toString();
      const bStr = (bVal ?? "").toString();
      const delta = aStr.localeCompare(bStr, undefined, { numeric: true });
      return sortConfig.direction === "asc" ? delta : -delta;
    });
    return copy;
  }, [rows, sortConfig]);

  // Default heartbeat exclusion ON when page is visited
  useEffect(() => {
    initializeHeartbeatExclusion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Load data effect with filters
  useEffect(() => {
    if (!token || !isFilterStoreInitialized) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");

    currentFetcher(token, {
      page,
      size: rowsPerPage,
      filters,
      signal: controller.signal,
    })
      .then((data) => {
        setRows(data?.content || []);
        setTotal(data?.totalElements ?? 0);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setError(err.message || "Failed to load logs.");
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [token, isFilterStoreInitialized, currentFetcher, page, rowsPerPage, filters]);

  const handleTabChange = (_, value) => {
    if (!value || value === activeTab) return;
    setActiveTab(value);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSort = (columnKey, direction) =>
    setSortConfig({ key: columnKey, direction });

  const handleRefresh = () => {
    const updatedFilters = { ...filters, ...getDefaultPast24hUtcRange() };
    setFilters(updatedFilters);
    setPage(0);
    setSessionRefreshTrigger((prev) => prev + 1);
  };

  const handleApplyFilters = async (nextFilters) => {
    setFilters(nextFilters);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    clearFilters();
    setPage(0);
  };

  const chipFilters = useMemo(() => {
    if (!filters) return {};
    const mapped = {};
    if (filters.timeFrom) {
      const localFrom = utcToLocal(filters.timeFrom);
      mapped.minTime = localFrom
        ? localFrom.format("DDMMMYY HH:mm")
        : dayjs(filters.timeFrom).format("DDMMMYY HH:mm");
    }
    if (filters.timeTo) {
      const localTo = utcToLocal(filters.timeTo);
      mapped.maxTime = localTo
        ? localTo.format("DDMMMYY HH:mm")
        : dayjs(filters.timeTo).format("DDMMMYY HH:mm");
    }
    // Concatenate sender and target for display
    if (filters.sendercompid && filters.targetcompid) {
      mapped.session = `${filters.sendercompid}-${filters.targetcompid}`;
    }
    (filters.textIncludes || []).forEach((text, idx) => {
      mapped[`textIncludes__${idx}`] = text;
    });
    (filters.textExcludes || []).forEach((text, idx) => {
      mapped[`textExcludes__${idx}`] = text;
    });
    return mapped;
  }, [filters]);

  const handleChipDelete = (key) => {
    removeFilter(key);
    setPage(0);
  };

  const handleChipClearAll = () => {
    handleClearAllFilters();
  };

  const handleDecodeClick = (row) => {
    setSelectedMessage(row);
    setDecoderModalOpen(true);
  };

  const handleCloseDecoder = () => {
    setDecoderModalOpen(false);
    setSelectedMessage(null);
  };

  const executeDownload = async (format = "xlsx") => {
    setCsvLoading(true);
    try {
      const result = await downloadFixLogs(
        token,
        currentFetcher,
        filters,
        hasActiveFilters,
        columns,
        format,
        activeTab,
        {
          sortConfig,
          sectionKey: `quickfix-${activeTab}`,
        }
      );

      if (result?.status === "empty") {
        setNoLogsAppliedFilters(result?.context?.appliedFilters || []);
        setNoLogsDialogOpen(true);
        return;
      }

      if (result?.status === "error") {
        const errorMessage = result?.error?.message || "Failed to download logs. Please try again.";
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Download failed:", error);
      const errorMessage = error?.message || "Failed to download logs. Please try again.";
      setError(errorMessage);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleDownloadCSV = async (format = "xlsx") => {
    const hasDateFilter = Boolean(filters?.timeFrom || filters?.timeTo);
    const shouldShowDownloadAttention = !hasDateFilter && total > MAX_DOWNLOAD_RECORDS;
    if (shouldShowDownloadAttention) {
      setPendingDownloadFormat(format);
      setDownloadConfirmOpen(true);
      return;
    }

    await executeDownload(format);
  };

  const handleProceedDownload = async () => {
    setDownloadConfirmOpen(false);
    await executeDownload(pendingDownloadFormat);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* NEW: session header row (now rendered fully by QuickFixSessionStatus) */}
      <QuickFixSessionStatus token={token} refreshTrigger={sessionRefreshTrigger} />

      {/* Separator: centered horizontal line with equal space above/below */}
      <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center" }}>
        <Box sx={{ flex: 1, borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}` }} />
      </Box>

      {/* Main header (logs) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          // pt: 1, // reduced from 2 to balance spacing around the separator
          pr: 2,
        }}
      >
        <Box
          sx={{
            pl: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Logs
          </Typography>

          <Tooltip title="Refresh logs">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {/* Filter Button */}
          <Tooltip title="Filter logs">
            <IconButton
              size="small"
              onClick={() => setFilterModalOpen(true)}
              sx={{
                color: hasActiveFilters ? tokens.palette.primary : "inherit",
                backgroundColor: hasActiveFilters
                  ? withAlpha(tokens.palette.primary, 0.1)
                  : "transparent",
              }}
            >
              <FilterAltOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Exclude Heartbeat Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={excludeHeartbeat}
                onChange={(e) => setExcludeHeartbeat(e.target.checked)}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: tokens.palette.primary,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: tokens.palette.primary,
                  },
                }}
              />
            }
            label="Exclude Heartbeat"
            sx={{
              ml: 1,
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
                fontWeight: 600,
                color: tokens.colors.fontColour,
              },
            }}
          />

          {hasActiveFilters && (
            <FilterChips
              input={chipFilters}
              onDeleteChip={handleChipDelete}
              onClearFilters={handleChipClearAll}
              readOnly={false}
              compact={false}
              maxVisibleChips={2}
            />
          )}
          {/* {hasActiveFilters && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearAllFilters}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                py: 0.25,
                px: 1,
                minHeight: "24px",
                borderRadius: "4px",
              }}
            >
              Clear Filters
            </Button>
          )} */}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
          }}
        >
          {/* Download Button */}
          <DownloadMenu
            csvLoading={csvLoading}
            onSelectFormat={handleDownloadCSV}
            tooltip="Download logs"
          />

          {/* Toggle group remains here */}
          <ToggleButtonGroup
            exclusive
            value={activeTab}
            onChange={handleTabChange}
            size="small"
            sx={{
              ml: { xs: 0, sm: 1 },
              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: 0,
                border: `1px solid ${tokens.colors.darkgray} !important`,
                "&:not(:first-of-type)": {
                  borderLeft: `1px solid ${tokens.colors.darkgray} !important`,
                  marginLeft: 0,
                },
              },
              "& .MuiToggleButton-root": {
                textTransform: "none",
                color: tokens.colors.fontColour,
                px: 1.25,
                height: 28,
                backgroundColor: "transparent",
                border: "none",
                "&:hover": {
                  backgroundColor: withAlpha(tokens.colors.white, 0.08),
                },
              },
              "& .MuiToggleButton-root.Mui-selected": {
                color: tokens.palette.primary,
                backgroundColor: `${withAlpha(
                  tokens.palette.primary,
                  0.12
                )} !important`,
                borderColor: `${tokens.palette.primary} !important`,
                zIndex: 2,
              },
              "& .MuiToggleButton-root.Mui-selected:first-of-type": {
                borderRightColor: "primary.main !important",
              },
              "& .MuiToggleButton-root.Mui-selected:last-of-type": {
                borderLeftColor: "primary.main !important",
              },
              "& .MuiToggleButton-root.Mui-selected + .MuiToggleButton-root": {
                borderLeftColor: `${tokens.palette.primary} !important`,
              },
            }}
          >
            {Object.entries(TAB_CONFIG).map(([key, tab]) => (
              <ToggleButton key={key} value={key}>
                {tab.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Always render table; table itself will show overlay loader */}
      <FixLogsTable
        rows={sortedRows}
        columns={columns}
        loading={loading}
        showNoLogs={!loading && !error && sortedRows.length === 0}
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        sectionKey={`quickfix-${activeTab}`}
        onDecodeClick={handleDecodeClick}
        decodeHighlightedRow={selectedMessage}
        decodeHighlightActive={decoderModalOpen}
      />

      {/* Filter Modal */}
      <QuickFixFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />

      {/* Decoder Modal */}
      <FixMessageDecoderModal
        open={decoderModalOpen}
        onClose={handleCloseDecoder}
        rawMessage={selectedMessage?.text}
        timestamp={selectedMessage?.time ? formatFixTimestamp(selectedMessage.time) : null}
      />

      <DownloadNoLogsPopup
        open={noLogsDialogOpen}
        onClose={() => setNoLogsDialogOpen(false)}
        appliedFilters={noLogsAppliedFilters}
      />

      <QuickFixDownloadConfirmModal
        open={downloadConfirmOpen}
        onProceed={handleProceedDownload}
        onClose={() => setDownloadConfirmOpen(false)}
        loading={csvLoading}
      />
    </Box>
  );
};

const DownloadMenu = ({ csvLoading, onSelectFormat, tooltip = "Download" }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={csvLoading}
          sx={{
            backgroundColor: tokens.colors.defaultGridRowColor,
            borderRadius: "8px",
            color: tokens.colors.fontColour,
            width: 42,
            height: 42,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          {csvLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => { handleClose(); onSelectFormat("csv"); }}>Download CSV</MenuItem>
        <MenuItem onClick={() => { handleClose(); onSelectFormat("xlsx"); }}>Download Excel</MenuItem>
      </Menu>
    </>
  );
};

export default QuickFixLogsPage;
